interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface StorageManagerOptions {
  prefix?: string;
  storage?: StorageLike;
}

interface StorageManager {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  getJson: <T>(key: string) => T | null;
  setJson: <T>(key: string, value: T) => void;
}

function resolveStorage(storage?: StorageLike): StorageLike | undefined {
  if (storage) {
    return storage;
  }

  try {
    return (globalThis as {localStorage?: StorageLike}).localStorage;
  } catch {
    return undefined;
  }
}

function createStorageManager({prefix, storage}: StorageManagerOptions = {}): StorageManager {
  const resolvedStorage = resolveStorage(storage);

  const toStorageKey = (key: string): string => {
    return prefix ? `${prefix}:${key}` : key;
  };

  const getItem = (key: string): string | null => {
    if (!resolvedStorage) {
      return null;
    }

    try {
      return resolvedStorage.getItem(toStorageKey(key));
    } catch {
      return null;
    }
  };

  const setItem = (key: string, value: string): void => {
    if (!resolvedStorage) {
      return;
    }

    try {
      resolvedStorage.setItem(toStorageKey(key), value);
    } catch {
      // no-op
    }
  };

  const removeItem = (key: string): void => {
    if (!resolvedStorage) {
      return;
    }

    try {
      resolvedStorage.removeItem(toStorageKey(key));
    } catch {
      // no-op
    }
  };

  const getJson = <T>(key: string): T | null => {
    const raw = getItem(key);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  };

  const setJson = <T>(key: string, value: T): void => {
    setItem(key, JSON.stringify(value));
  };

  return {
    getItem,
    setItem,
    removeItem,
    getJson,
    setJson
  };
}

export {createStorageManager};
export type {StorageLike, StorageManager, StorageManagerOptions};
